import FunderApplicantLayoutHeader from '@components/header/FunderApplicantLayoutHeader';
import FunderLayoutHeader, { IFunderLayoutHeader } from '@components/header/FunderLayoutHeader';
import { changeLanguage } from '@components/i18n/i18n';
import NonDashboardRoutes from '@/config/non-dashboard-routes';
import { getFreeCashBalanceSummary, initFunderPage } from '@services/funder/action';
import { Layout, Spin } from 'antd';
import memoizeOne from 'memoize-one';
import { withRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import Header from './header/Header';
import { theme } from './styles/GlobalStyles';
import { Container, Inner } from './styles/Page';
import { IReduxInitialState } from '@interface/IReduxInitialState';

const { Content } = Layout;
const NonDashboardRoutesRegExp = NonDashboardRoutes.map(element => RegExp(element));
const RegistrationRoute = new RegExp('/registration/')

const checkNonDashboardRoutes = memoizeOne((path) => (path === '/' || NonDashboardRoutesRegExp.some((element) => element.test(path))));
const checkRegistrationRoutes = memoizeOne((path) => RegistrationRoute.test(path));
const renderPageHeaderMemoizeOne = memoizeOne((isRegistrationRoute, isNotDashboard, params: IFunderLayoutHeader) => LayoutHeader(isRegistrationRoute, isNotDashboard, params));


function RenderHeader({ isNotDashboard, funderName }) {
  if (!funderName || isNotDashboard)
    return null;
  return <Header />;
}

const DefaultFunderParams = Object.freeze({
  funderName: 'NA',
  freeCashHKD: undefined,
  freeCashUSD: undefined,
}) as IFunderLayoutHeader;

function LayoutHeader(isRegistrationRoute, isNotDashboard, { funderName, path, freeCashHKD, freeCashUSD }: IFunderLayoutHeader) {

  if (isRegistrationRoute)
    return <FunderApplicantLayoutHeader name={funderName} />;

  if (!funderName || isNotDashboard || path == '/404' || path == '/error')
    return null;

  return <FunderLayoutHeader funderName={funderName} path={path} freeCashHKD={freeCashHKD} freeCashUSD={freeCashUSD} />;
}

const Page = ({ dispatch, router, children, funderName, freeCashBalance, language }) => {

  const [loading, setLoading] = useState(true);
  const [funderHeaderParams, setFunderHeaderParams] = useState(DefaultFunderParams);

  const path = router?.pathname;
  const isNotDashboard = checkNonDashboardRoutes(path);
  const isRegistrationRoute = checkRegistrationRoutes(path);

  //Loading hooks from One-React
  useEffect(() => { setTimeout(() => setLoading(false), 1000) }, [loading]);

  //Initialize page language
  useEffect(() => {
    if (language) {
      changeLanguage(language, dispatch);
    }
  }, [language]);

  //Update
  useEffect(() => {
    if (isRegistrationRoute || isNotDashboard)
      return;

    if (!funderName)
      dispatch(initFunderPage());
    else
      dispatch(getFreeCashBalanceSummary());

  }, [router?.pathname]);

  useEffect(() => {
    if (freeCashBalance)
      setFunderHeaderParams({
        funderName,
        freeCashHKD: freeCashBalance.hkd,
        freeCashUSD: freeCashBalance.usd
      })
  }, [freeCashBalance, funderName]);

  const funderParams = {
    ...funderHeaderParams,
    path
  };

  return (
    <Spin tip="Loading..." size="large" spinning={loading}>
      <ThemeProvider theme={theme}>
        <Container>

          <Layout>

            <RenderHeader isNotDashboard={isNotDashboard} funderName={funderName} />

            <Content>
              {renderPageHeaderMemoizeOne(isRegistrationRoute, isNotDashboard, funderParams)}
              {isNotDashboard ? children : <Inner>{children}</Inner>}
            </Content>
          </Layout>

        </Container>
      </ThemeProvider>
    </Spin>
  );
};

function mapStoreToProps(state: IReduxInitialState) {
  const {
    FunderApplicantReducer: { profile },
    FunderReducer: {
      funder,
      freeCashBalance
    },
    UserReducer
  } = state;
  return {
    funderName: funder.name || profile.name,
    freeCashBalance,
    language: UserReducer.profile ? UserReducer.profile.locale : undefined
  }
}

export default connect(mapStoreToProps)(withRouter(Page));
